import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { Typography } from '@material-ui/core';

import { SpaceBetweenContainer, HealtheraButton, InputField } from '../../../../healtheraComponents';
import { RichTextEditor, ImageUpload } from '../../../Shared/Form';
import { DangerButton } from '../../../Shared/Button';
import { FormContainer, Column, Section, ErrorMessage } from '../../styled-components';
import { getFormData } from '../../../../utils/formatters';
import QuestionnaireSelector from './components/QuestionnaireSelectorForm';

export default function ServiceConditionForm({ condition, upsertServiceCondition, deleteCondition }) {
  const isExistingCondition = Boolean(condition?._id);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      name: condition?.name || '',
      internal_name: condition?.internal_name || '',
      description_short: condition?.description_short || '',
      description: condition?.description || '',
      image_url: condition?.image_url || '',
      questionnaire_id: condition?.questionnaire_id || '',
    }
  });

  const [uploadedConditionImage] = watch('image') || [];
  const conditionImage = uploadedConditionImage
    ? URL.createObjectURL(uploadedConditionImage)
    : condition?.image_url;

  async function onSubmit(data) {
    const updateConditionPayload = {};
    if (data.name && data.name.trim() !== condition?.name) {
      updateConditionPayload.name = data.name.trim();
    }

    if (data.internal_name && data.internal_name.trim() !== condition?.internal_name) {
      updateConditionPayload.internal_name = data.internal_name.trim();
    }

    if (data.description_short && data.description_short.trim() !== condition?.description_short) {
      updateConditionPayload.description_short = data.description_short.trim();
    }

    if (data.description && data.description !== condition?.description) {
      updateConditionPayload.description = data.description;
    }

    if (data.image) updateConditionPayload.image = data.image[0];

    if (data.questionnaire_id !== condition?.questionnaire_id) {
      updateConditionPayload.questionnaire_id = data.questionnaire_id;
    }

    const formData = updateConditionPayload.image
      ? getFormData(updateConditionPayload)
      : null;

    try {
      await upsertServiceCondition({
        _id: condition?._id,
        updatePayload: updateConditionPayload,
        formData
      });
    } catch (error) {
      // This is handled in mutation
    }
  }

  function SubmitButton() {
    return (
      <HealtheraButton onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
        {isExistingCondition ? 'Save Changes' : 'Create Condition'}
      </HealtheraButton>
    );
  }

  return (
    <Container>
      <SpaceBetweenContainer>
        <Typography variant="h5">Condition settings</Typography>
        <SubmitButton />
      </SpaceBetweenContainer>
      <FormContainer>
        <Column>
          <Section>
            <header>Primary details</header>
            <Controller
              control={control}
              name="internal_name"
              rules={{
                required: { value: true, message: 'Condition name is required' },
                pattern: { value: /\S/, message: 'Condition name must be non empty string' }
              }}
              render={({ field: { ref, ...otherField } }) => (
                <div>
                  <InputField
                    {...otherField}
                    margintop="0"
                    label="Condition name"
                    inputRef={ref}
                    required
                    error={errors?.internal_name?.message}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="name"
              rules={{
                required: { value: true, message: 'In app condition name is required' },
                pattern: { value: /\S/, message: 'In app condition name must be non empty string' }
              }}
              render={({ field: { ref, ...otherField } }) => (
                <div>
                  <InputField
                    {...otherField}
                    margintop="0"
                    label="In app condition name"
                    inputRef={ref}
                    required
                    error={errors?.name?.message}
                  />
                </div>
              )}
            />
          </Section>
          <Section>
            <header>Description</header>
            <Controller
              control={control}
              name="description_short"
              rules={{
                required: 'Short description is required',
                pattern: { value: /\S/, message: 'Short description must be non empty string' },
                maxLength: 'Short description should not exceed 300 characters'
              }}
              render={({ field: { ref, ...otherField } }) => (
                <div style={{ position: 'relative' }}>
                  <InputField
                    {...otherField}
                    margintop="0"
                    label="Short description"
                    inputRef={ref}
                    required
                    limit={300}
                    error={errors?.description_short?.message}
                  />
                </div>
              )}
            />
            <div>
              <Typography style={{ fontSize: '13px', color: '#757575', marginBottom: '12px' }}>
                Full description *
              </Typography>
              <Controller
                control={control}
                name="description"
                rules={{ required: 'Full description is required' }}
                render={({ field: { value, onChange }, formState }) => (
                  <>
                    <RichTextEditor value={value} onChange={onChange} />
                    {formState.errors.description && (
                      <ErrorMessage>{formState.errors.description.message}</ErrorMessage>
                    )}
                  </>
                )}
              />
            </div>
          </Section>
        </Column>
        <Column>
          <Section>
            <header>Image</header>
            <ImageUpload
              imageUrl={conditionImage}
              replace={Boolean(condition?.image_url)}
              inputProps={register('image', {
                required: condition?.image_url ? false : 'Image is required'
              })}
              error={errors?.image?.message}
            />
          </Section>
          <Section>
            <header>Questionnaire</header>
            <QuestionnaireSelector questionnaireId={condition?.questionnaire_id} control={control} />
          </Section>
        </Column>
        <ActionSection style={{ gridColumn: 'span 2' }}>
          <DangerButton onClick={() => deleteCondition(condition._id)}>Delete</DangerButton>
          <SubmitButton />
        </ActionSection>
      </FormContainer>
    </Container>
  );
};

const Container = styled.div`
  margin: 40px auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ActionSection = styled.section`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
`;
